import { Button, Input } from "@izicvs/ui";
import type { ResumeDto } from "@izicvs/dto";
import { defaultResumeData, type ResumeData } from "@izicvs/schema";
import { cn, isPremiumTemplate, templatesList } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ClipboardText,
  Crown,
  DownloadSimple,
  MagicWand,
  NotePencil,
  X,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router";

import { toast } from "@/client/hooks/use-toast";
import { useGenerateResume } from "@/client/services/ai/generate-resume";
import { useImportResume } from "@/client/services/resume/import";
import { applyTemplate } from "@/client/services/resume/templates";
import { useDialog } from "@/client/stores/dialog";

import { ResumePreviewFrame } from "../resumes/_layouts/grid/_components/resume-preview-frame";

type Path = "ai" | "scratch";

const scratchData = (name: string, headline: string, email: string): ResumeData => {
  const data: ResumeData = structuredClone(defaultResumeData);
  data.basics.name = name.trim();
  data.basics.headline = headline.trim();
  data.basics.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) ? email.trim() : "";
  return data;
};

const StepDots = ({ step }: { step: number }) => (
  <div className="flex items-center gap-2">
    {[0, 1, 2].map((index) => (
      <div
        key={index}
        className={cn(
          "h-1.5 rounded-full transition-all duration-300",
          index === step ? "w-8 bg-primary" : index < step ? "w-1.5 bg-primary" : "w-1.5 bg-border",
        )}
      />
    ))}
  </div>
);

export const CreatePage = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { generate, loading: generating } = useGenerateResume();
  const { importResume, loading: importing } = useImportResume();
  const importDialog = useDialog("import");

  const [step, setStep] = useState(0);
  const [path, setPath] = useState<Path | null>(null);
  const [aiInput, setAiInput] = useState("");
  const [name, setName] = useState("");
  const [headline, setHeadline] = useState("");
  const [email, setEmail] = useState("");
  const [data, setData] = useState<ResumeData | null>(null);
  const [title, setTitle] = useState("");
  const [template, setTemplate] = useState<string>("aurora");
  const [creating, setCreating] = useState(false);

  const busy = generating || importing || creating;

  // A ready-to-use example so users are not staring at a blank field.
  const examplePrompt = t`I'm Karim Benali, a DevOps engineer in Lyon (karim.benali@example.com, 06 12 34 56 78). 6 years of experience. At OVHcloud (2020 to today) I automate Kubernetes deployments and cut release time by 40%. Before that, at Capgemini (2018-2020), I set up GitLab CI/CD pipelines. Skills: AWS, Terraform, Docker, Kubernetes, Python, Ansible. Engineering degree from INSA Lyon, 2018. Languages: French (native), English (fluent).`;

  // Live preview reflects the working data with the chosen template applied.
  const previewData = useMemo(() => (data ? applyTemplate(data, template) : null), [data, template]);

  const goImport = () => {
    // Re-use the proven importer; the imported resume lands in the library.
    navigate("/dashboard/resumes");
    importDialog.open("create");
  };

  const startFromScratch = () => {
    // Straight to a blank builder for people who want to skip the wizard.
    createResume(structuredClone(defaultResumeData), t`My resume`);
  };

  const onGenerate = async () => {
    if (aiInput.trim().length < 20) return;
    try {
      const result = await generate({ input: aiInput.trim(), language: i18n.locale });
      setData(result.data);
      setTitle(result.title);
      setStep(2);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Could not generate your resume`,
        description: (error as Error).message,
      });
    }
  };

  const onScratchNext = () => {
    if (!name.trim()) return;
    setData(scratchData(name, headline, email));
    setTitle(name.trim());
    setStep(2);
  };

  const createResume = async (finalData: ResumeData, finalTitle: string) => {
    setCreating(true);
    try {
      const resume = await importResume({ title: finalTitle || t`My resume`, data: finalData });
      void navigate(`/builder/${resume.id}`);
    } catch (error) {
      setCreating(false);
      toast({
        variant: "error",
        title: t`Could not create your resume`,
        description: (error as Error).message,
      });
    }
  };

  const onFinish = () => {
    if (!data) return;
    void createResume(applyTemplate(data, template), title);
  };

  const back = () => {
    if (step === 0) {
      void navigate("/dashboard/resumes");
      return;
    }
    setStep((value) => Math.max(0, value - 1));
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <Helmet>
        <title>
          {t`Create a resume`} - {t`Izicvs`}
        </title>
      </Helmet>

      {/* Top bar: back, progress, close */}
      <header className="flex items-center justify-between border-b px-5 py-4">
        <Button variant="ghost" size="sm" onClick={back} disabled={busy}>
          <ArrowLeft className="mr-1.5 size-4" />
          {step === 0 ? t`Cancel` : t`Back`}
        </Button>
        <StepDots step={step} />
        <Button asChild variant="ghost" size="icon">
          <Link to="/dashboard/resumes" aria-label={t`Close`}>
            <X className="size-5" />
          </Link>
        </Button>
      </header>

      <main className="flex flex-1 overflow-hidden">
        {/* Left: step content */}
        <div className="flex flex-1 items-center justify-center overflow-y-auto p-6">
          <div className="w-full max-w-xl">
            <AnimatePresence mode="wait">
              {step === 0 && (
                <motion.div
                  key="step-path"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <div className="space-y-2 text-center">
                    <h1 className="text-3xl font-bold tracking-tight">{t`Let's build your resume`}</h1>
                    <p className="opacity-70">{t`Choose how you want to start.`}</p>
                  </div>

                  <div className="grid gap-3">
                    <PathCard
                      icon={MagicWand}
                      title={t`Create with AI`}
                      description={t`Describe yourself and let the AI draft a full resume.`}
                      badge={t`Fastest`}
                      onClick={() => {
                        setPath("ai");
                        setStep(1);
                      }}
                    />
                    <PathCard
                      icon={DownloadSimple}
                      title={t`Import an existing resume`}
                      description={t`LinkedIn, JSON Resume, or an Izicvs file.`}
                      onClick={goImport}
                    />
                    <PathCard
                      icon={NotePencil}
                      title={t`Start from scratch`}
                      description={t`Enter a few details and pick a template.`}
                      onClick={() => {
                        setPath("scratch");
                        setStep(1);
                      }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={startFromScratch}
                    disabled={busy}
                    className="mx-auto block text-sm opacity-50 underline-offset-4 transition-opacity hover:opacity-100 hover:underline"
                  >
                    {t`Or open a blank editor`}
                  </button>
                </motion.div>
              )}

              {step === 1 && path === "ai" && (
                <motion.div
                  key="step-ai"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-5"
                >
                  <div className="space-y-2">
                    <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
                      <MagicWand className="text-primary" />
                      {t`Tell us about yourself`}
                    </h1>
                    <p className="text-sm opacity-70">
                      {t`Describe your background, or paste an old resume or your LinkedIn profile. The AI never invents facts.`}
                    </p>
                  </div>
                  <textarea
                    value={aiInput}
                    autoFocus
                    rows={9}
                    disabled={busy}
                    placeholder={t`e.g. I'm Marie, a frontend developer with 4 years of experience at Acme and Globex. I led a redesign that lifted conversion by 30%, and I studied computer science at Paris-Saclay...`}
                    className="w-full resize-none rounded-lg border bg-background p-3 text-sm leading-relaxed outline-none ring-primary focus:ring-2"
                    onChange={(event) => setAiInput(event.target.value)}
                  />

                  {/* Copyable example prompt to remove the blank-page fear */}
                  <div className="rounded-lg border border-dashed bg-secondary-accent/20 p-3">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide opacity-50">
                        {t`Example`}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setAiInput(examplePrompt)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-opacity hover:opacity-80"
                      >
                        <ClipboardText className="size-3.5" />
                        {t`Use this example`}
                      </button>
                    </div>
                    <p className="line-clamp-3 text-xs leading-relaxed opacity-60">{examplePrompt}</p>
                  </div>

                  <Button
                    size="lg"
                    className="w-full"
                    disabled={busy || aiInput.trim().length < 20}
                    onClick={onGenerate}
                  >
                    <MagicWand className="mr-2" />
                    {generating ? t`Generating your resume...` : t`Generate my resume`}
                    {!generating && <ArrowRight className="ml-2" />}
                  </Button>
                  <p className="text-center text-xs opacity-50">
                    {t`This uses your AI allowance. Pro and Autopilot get more.`}
                  </p>
                </motion.div>
              )}

              {step === 1 && path === "scratch" && (
                <motion.div
                  key="step-scratch"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-5"
                >
                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold tracking-tight">{t`The essentials`}</h1>
                    <p className="text-sm opacity-70">
                      {t`Just enough to get going. You can complete everything in the editor.`}
                    </p>
                  </div>
                  <div className="grid gap-3">
                    <label className="grid gap-1.5">
                      <span className="text-sm font-medium">{t`Full name`}</span>
                      <Input
                        autoFocus
                        value={name}
                        placeholder={t`Marie Dupont`}
                        onChange={(event) => setName(event.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && onScratchNext()}
                      />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="text-sm font-medium">{t`Professional title`}</span>
                      <Input
                        value={headline}
                        placeholder={t`Frontend Developer`}
                        onChange={(event) => setHeadline(event.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && onScratchNext()}
                      />
                    </label>
                    <label className="grid gap-1.5">
                      <span className="text-sm font-medium">{t`Email`}</span>
                      <Input
                        type="email"
                        value={email}
                        placeholder={t`marie@email.com`}
                        onChange={(event) => setEmail(event.target.value)}
                        onKeyDown={(event) => event.key === "Enter" && onScratchNext()}
                      />
                    </label>
                  </div>
                  <Button size="lg" className="w-full" disabled={!name.trim()} onClick={onScratchNext}>
                    {t`Choose a template`}
                    <ArrowRight className="ml-2" />
                  </Button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step-template"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-5"
                >
                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold tracking-tight">{t`Pick your style`}</h1>
                    <p className="text-sm opacity-70">
                      {t`Choose a template. You can change it anytime in the editor.`}
                    </p>
                  </div>

                  <div className="grid max-h-[52vh] grid-cols-3 gap-4 overflow-y-auto p-2">
                    {templatesList.map((tpl) => {
                      const premium = isPremiumTemplate(tpl);
                      return (
                        <button
                          key={tpl}
                          type="button"
                          onClick={() => setTemplate(tpl)}
                          className={cn(
                            "relative aspect-[1/1.4142] overflow-hidden rounded-lg bg-secondary ring-primary ring-offset-2 ring-offset-background transition-all hover:ring-2",
                            template === tpl ? "ring-2" : "ring-1 ring-black/10",
                          )}
                        >
                          <img
                            src={`/templates/jpg/${tpl}.jpg`}
                            alt={tpl}
                            className="size-full object-cover object-top"
                            onError={(event) => {
                              event.currentTarget.style.visibility = "hidden";
                            }}
                          />
                          {premium && (
                            <span className="absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase text-primary-foreground">
                              <Crown className="size-2.5" />
                              {t`Pro`}
                            </span>
                          )}
                          {template === tpl && (
                            <span className="absolute bottom-1 right-1 flex size-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                              <Check className="size-3" weight="bold" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <Button size="lg" className="w-full" disabled={busy} onClick={onFinish}>
                    {creating ? t`Creating your resume...` : t`Create my resume`}
                    {!creating && <ArrowRight className="ml-2" />}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="hidden w-[42%] items-center justify-center border-l bg-secondary-accent/20 p-8 lg:flex">
          {previewData ? (
            <motion.div
              key={template}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="aspect-[1/1.4142] w-full max-w-sm overflow-hidden rounded-xl border bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_20px_40px_-12px_rgba(16,24,40,0.18)]"
            >
              <ResumePreviewFrame resume={{ data: previewData } as ResumeDto} />
            </motion.div>
          ) : (
            <div className="max-w-sm text-center opacity-40">
              <MagicWand className="mx-auto size-10" />
              <p className="mt-3 text-sm">{t`Your resume preview will appear here.`}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const PathCard = ({
  icon: Icon,
  title,
  description,
  badge,
  onClick,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  badge?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex items-center gap-4 rounded-2xl border p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[0_1px_2px_rgba(16,24,40,0.06),0_10px_24px_-8px_rgba(16,24,40,0.16)]"
  >
    <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
      <Icon className="size-5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span className="font-semibold">{title}</span>
        {badge && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
            {badge}
          </span>
        )}
      </span>
      <span className="block text-sm opacity-60">{description}</span>
    </span>
    <ArrowRight className="size-5 shrink-0 opacity-30 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
  </button>
);
